import * as React from 'react';
import { TpTooltip } from "@tranquility-ai/theme-components/tooltip";
import { Button } from "@tranquility-ai/theme-components/button";
import {
  DownloadSimpleIcon,
  XIcon,
  PlusSquareIcon,
  MagnifyingGlassPlusIcon
} from "@phosphor-icons/react";
import { Popover, PopoverTrigger, PopoverContent } from "@heroui/popover";
import { MEDIA_PREVIEW_OVERLAY_SURFACE_CLASS } from '@/shared/inverse-overlay-surfaces';
import {
  handleDownload,
  extractPathAfterBulk,
  isRestrictedFileType,
  resolvePreferredMediaDisplayForSource,
  isInlineJsonCitationDisplayable,
  isArchiveMemberSearchablePath,
} from '@/shared/functions';
import { useEffect, useRef, useContext, useState } from 'react';
import Select from "react-select";
import { Source } from '@/shared/types';
import { SourcesSelectStyles } from '@/shared/styleDefs';
import { useDebounce } from 'use-debounce';
import { MediaType, MediaSource } from '@/shared/types';
import { isDisplayableMediaType } from '@/shared/constants';
import { isSpreadsheetMediaItem } from '@/shared/media-preview-layout';
import {
  PATH_DISPLAY_WHITESPACE_CLASSNAME,
  PATH_SEGMENT_TRUNCATE_CLASSNAME,
} from '@/shared/uiConstants';
import MediaDisplay from '../ui/mediaDisplay';
import { getMediaType } from '@/shared/functions';
import { FullScreenContext } from '@/app/contexts';
import { useJsonMediaDisplayEnabled } from '@/hooks/useMediaPlayerTransition';
import { useTimePilotT, type TranslateFn } from '@/i18n/useTimePilotT';

export const sourceObjectTypes = [ 'image', 'video', 'audio', 'pdf', 'url'];

type OptionType = {
  value: MediaSource;
  label: string;
};

/** Page may be stored as sourceContext.pageNumber, sourceContext.page (legacy), or top-level pageNumber. */
const resolveSourcePageRef = (source: MediaSource) =>
  source.sourceContext?.pageNumber ?? source.sourceContext?.page ?? source.pageNumber;

/** Audio/video: only start/end (or nothing). Other types: page only (never time indices). */
function formatSimplifiedSourceRowSuffix(sourceObject: MediaSource, t: TranslateFn): string {
  const mt = getMediaType(sourceObject, sourceObject.fileInfo);
  if (mt === 'audio' || mt === 'video') {
    const parts: string[] = [];
    if (sourceObject.sourceContext?.startTime != null && sourceObject.sourceContext.startTime !== '') {
      parts.push(t('sources.rowStart', { time: String(sourceObject.sourceContext.startTime) }));
    }
    if (sourceObject.sourceContext?.endTime != null && sourceObject.sourceContext.endTime !== '') {
      parts.push(t('sources.rowEnd', { time: String(sourceObject.sourceContext.endTime) }));
    }
    return parts.length ? `, ${parts.join(', ')}` : '';
  }
  const pageRef = resolveSourcePageRef(sourceObject);
  return pageRef != null && pageRef !== '' ? `, ${t('sources.rowPage', { page: pageRef })}` : '';
}

function formatSimplifiedChatSearchOptionLabel(source: MediaSource, t: TranslateFn): string {
  const mt = getMediaType(source, source.fileInfo);
  if (mt === 'audio' || mt === 'video') {
    const bits: string[] = [];
    if (source.sourceContext?.startTime) bits.push(t('sources.rowTime', { time: String(source.sourceContext.startTime) }));
    if (source.sourceContext?.endTime) bits.push(t('sources.rowEnd', { time: String(source.sourceContext.endTime) }));
    const suffix = bits.length ? `, ${bits.join(', ')}` : '';
    return `${source.sourceUrl}${suffix}`;
  }
  const pageRef = resolveSourcePageRef(source);
  const pageSuffix = pageRef != null && pageRef !== '' ? `, ${t('sources.chatPage', { page: pageRef })}` : '';
  return `${source.sourceUrl}${pageSuffix}`;
}

const limitFilter = (array: MediaSource[], condition: (item: MediaSource) => boolean, maxSize: number) => {
  if (!maxSize || maxSize > array.length) {
    maxSize = array.length;
  }
  let count = 0;
  let i = 0;
  let filteredArray: MediaSource[] = [];
  while (count < maxSize && i < array.length ) {
    if (condition(array[i])) {
      filteredArray.push(array[i]);
      count++;
    }
    i++;
  }
  return filteredArray;
}

function normalizeSimplifiedSources(value: MediaSource[]): MediaSource[] {
  return value.filter((source) => source.sourceUrl && source.sourceUrl.trim() !== '');
}

function simplifiedSourceListsEqual(a: MediaSource[], b: MediaSource[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((source, index) => {
    const other = b[index];
    return (
      source.sourceUrl === other.sourceUrl &&
      source.truncatedFilePath === other.truncatedFilePath &&
      source.pageNumber === other.pageNumber
    );
  });
}

interface SimlifiedSourcesProps {
  value: MediaSource[],
  slide?: number,
  column?: string,
  isInModal?: boolean,
  isInPresentationView?: boolean,
  handleChange?: any,
  editingEnabled?: boolean,
  caseId?: string
  handleSourceChanges?: any
  sourcesFromChat?: MediaSource[],
}

const SimplifiedSources = (props: SimlifiedSourcesProps) => {
  const {
    value,
    slide,
    column,
    isInModal,
    isInPresentationView,
    handleChange,
    editingEnabled,
    caseId,
    handleSourceChanges,
    sourcesFromChat
  } = props;
  const [sourceFromSessionStorage, setSourceFromSessionStorage] = useState<Source | null>(null);
  const [newSources, setNewSources] = useState<MediaSource[]>(value);
  const [inputValue, setInputValue] = useState<string | null>(null);
  const [sourcesForDropdown, setSourcesForDropdown] = useState<MediaSource[]>([]);
  const [previewPopoverOpen, setPreviewPopoverOpen] = useState(-1);

  const { isFullscreen } = useContext(FullScreenContext);
  const jsonMediaDisplayEnabled = useJsonMediaDisplayEnabled();
  const t = useTimePilotT();

  const [searchTerm] = useDebounce(inputValue, 500);

  const selectRef = useRef<HTMLDivElement>(null);

  const getInputValue = (elem: Element) => {
    if (elem) {
      const inputValue = elem.getAttribute('data-value');
      setInputValue(inputValue);
    }
    return null;
  }

  useEffect(() => {
    if (isFullscreen) {
      setPreviewPopoverOpen(-1);
    }
  }, [isFullscreen]);

  useEffect(() => {
    if (selectRef.current) {
      const inputContainer = selectRef.current.getElementsByClassName('react-select__input-container')[0];
      const handler = (e: KeyboardEvent) => getInputValue(inputContainer);
      if (inputContainer) {
        window.addEventListener('keyup', handler);
        return () => {
          window.removeEventListener('keyup', handler);
        }
      }
    }
  }, [selectRef.current]);

  useEffect(() => {
    if (sourcesFromChat && searchTerm && searchTerm.length > 2) {
      const filteredSources = limitFilter(sourcesFromChat, (source) => {
        return !!source.sourceUrl && source.sourceUrl.toLowerCase().includes(searchTerm.toLowerCase());
      }, 100);
      setSourcesForDropdown(filteredSources);
    }
  }, [sourcesFromChat, searchTerm]);

  const availableSource = sessionStorage.getItem(`source`);

  useEffect(() => {
    const sourcesIn = normalizeSimplifiedSources(value);
    setNewSources((prev) => (simplifiedSourceListsEqual(prev, sourcesIn) ? prev : sourcesIn));
  }, [value]);

  useEffect(() => {
    if (availableSource) {
      const sourceFromSessionStorage = JSON.parse(availableSource);
      const sourceCaseId = sourceFromSessionStorage?.caseId?.trim();
      if (sourceCaseId === caseId) {
        setSourceFromSessionStorage(sourceFromSessionStorage.source);
      } else {
        setSourceFromSessionStorage(null);
      }
    } else {
      setSourceFromSessionStorage(null);
    }
  }, [availableSource, caseId]);

  const addSourceFromSessionStorage = () => {
    if (sourceFromSessionStorage) {
      const moreSources = newSources ? [...newSources] : [];
      const sourceType = extractPathAfterBulk(sourceFromSessionStorage.sourceFilePath || '').sourceType || 'Unknown';
      const sourceObject = {
        sourceType: sourceType,
        sourceUrl: sourceFromSessionStorage.sourceFilePath,
        truncatedFilePath: sourceFromSessionStorage.truncatedFilePath,
        sourceContext: sourceFromSessionStorage.sourceContext,
        pageNumber: sourceFromSessionStorage.pageNumber
      };
      moreSources.push(sourceObject);
      setNewSources(moreSources);
      handleSourceChanges(moreSources);
    }
  };

  const onSourceSelect = (source: MediaSource) => {
    const moreSources = newSources ? [...newSources] : [];
    if (source) {
      moreSources.push(source);
      setNewSources(moreSources);
      handleSourceChanges(moreSources);
    }
  };

  const removeSource = (index: number) => {
    const lessSources = newSources.filter((_, i) => i !== index);
    setNewSources(lessSources);
    handleSourceChanges(lessSources);
  };

  if (isInModal || isInPresentationView) {
    return (
      <div className="box-border flex flex-col justify-left w-full">
        {editingEnabled && (
          <>
            {sourcesFromChat && sourcesFromChat.length > 0 && (
              <div className='flex flex-row justify-start items-center gap-3 w-full p-0 m-0'>
                <div ref={selectRef} className='box-border whitespace-nowrap z-900'>
                  <label className="flex text-white/100 font-light tracking-wider text-[14px] m-0 mb-2 p-0">{t('sources.heading')}</label>
                  <Select<OptionType, false>
                    styles={SourcesSelectStyles as any}
                    className="zIndexSelect-19"
                    classNamePrefix='react-select'
                    aria-label={t('sources.addFromChat')}
                    placeholder={t('sources.searchPlaceholder')}
                    noOptionsMessage={() => t('sources.noMatch')}
                    options={sourcesForDropdown?.map((source, index) => ({
                      value: source,
                      label: formatSimplifiedChatSearchOptionLabel(source, t),
                    }))}
                    onChange={(option) => option && onSourceSelect(option.value)}
                    components={{
                      Option: ({ data, innerProps }: { data: OptionType; innerProps: any }) => (
                        <div
                          {...innerProps}
                          className="box-border p-0 flex flex-row justify-left bg-[#0F1D32] rounded-sm"
                        >
                          <div className="box-border w-full rounded-sm py-[5px] px-[3px] bg-slate-800 text-[14px] cursor-pointer
                            dir-rtl whitespace-nowrap truncate border-1 border-white border-solid opacity-100% z-1000"
                          >
                            &lrm;{data.label}
                          </div>
                        </div>
                      )
                    }}
                  />
                </div>
                {sourceFromSessionStorage && (
                  <div className='flex flex-row justify-left items-center gap-1 w-[20px] p-0 m-0'>
                    <TpTooltip content={t('sources.addLastCopied')} placement='top'>
                      <Button
                        iconOnly
                        size="sm"
                        style="text"
                        color="light"
                        background="inverse"
                        className="m-0"
                        aria-label={t('sources.addLastCopied')}
                        onClick={addSourceFromSessionStorage}
                      ><PlusSquareIcon />
                      </Button>
                    </TpTooltip>
                  </div>
                )}
              </div>
            )}
            {newSources && newSources.length < 1 && (
              <div className='text-white/100 font-light tracking-wider text-[14px] mt-2 p-0 italic'>{t('sources.noneSpecified')}</div>
            )}
          </>
        )}
        <ol className='box-border flex flex-col gap-1 w-full p-0 m-0 sm:max-h-[100px] overflow-auto custom-scrollbar2 mt-1 list-decimal list-outside pr-2'>
          {newSources && newSources.length > 0 && newSources.map((sourceObject: MediaSource, index: number) => {
            // Citation-aware preview resolution: for container parents (mbox,
            // zip, pst, ufdr) ``resolvePreferredMediaDisplayForSource``
            // returns the cited extract (``previewFilePath`` /
            // ``dispatchedFilePath``) rather than the unplayable container,
            // so the eye affordance works for things like audio attachments
            // cited inside an mbox citation. For direct media citations the
            // resolver returns the same source path so behaviour is
            // unchanged.
            const preferred = resolvePreferredMediaDisplayForSource(
              sourceObject,
              sourceObject.fileInfo,
            );
            const existingUrl = (sourceObject.mediaUrl || sourceObject.mediaSrc || '').trim();
            const existingType = existingUrl
              ? getMediaType(
                  {
                    mediaType: sourceObject.mediaType || 'unknown',
                    mediaUrl: existingUrl,
                  } as MediaSource,
                  sourceObject.fileInfo,
                )
              : ('unknown' as MediaType);
            const existingDisplayable =
              !!existingUrl &&
              !isArchiveMemberSearchablePath(existingUrl) &&
              isDisplayableMediaType(
                existingType !== 'unknown' ? existingType : sourceObject.mediaType,
                { jsonMediaDisplay: jsonMediaDisplayEnabled },
              );
            const preferredDisplayable =
              !!preferred.mediaUrl &&
              !isArchiveMemberSearchablePath(preferred.mediaUrl) &&
              isDisplayableMediaType(preferred.mediaType, {
                jsonMediaDisplay: jsonMediaDisplayEnabled,
              });
            const hasDisplayableUrl = preferredDisplayable || existingDisplayable;
            const contentOnlyJson =
              !hasDisplayableUrl &&
              isInlineJsonCitationDisplayable(
                sourceObject,
                preferred.mediaType,
                jsonMediaDisplayEnabled,
              );
            const isPreviewable = hasDisplayableUrl || contentOnlyJson;
            const previewMediaUrl = preferredDisplayable
              ? preferred.mediaUrl!
              : existingDisplayable
                ? existingUrl
                : sourceObject.sourceUrl;
            const previewMediaType = preferredDisplayable
              ? preferred.mediaType
              : existingDisplayable
                ? ((existingType !== 'unknown' ? existingType : sourceObject.mediaType) ||
                  preferred.mediaType)
                : contentOnlyJson
                  ? 'json'
                  : preferred.mediaType;
            // Video/audio previews need room for the player controls *and* a
            // side-by-side transcript. A wider box (matches EVENT_MODAL_SIDE_*
            // sizing: ~376px player + ~318px transcript + gap) keeps "beside"
            // usable instead of squeezing the video down until controls overlap.
            const isTranscriptCapablePreview = previewMediaType === 'video' || previewMediaType === 'audio';
            // Paged documents need the source-card fit box: the generic layout
            // sizes the page to the full box without the toolbar, so it
            // overflows into misaligned scrollbars and a left-aligned page nav.
            const isPagedDocumentPreview =
              previewMediaType === 'pdf' || previewMediaType === 'docx' || previewMediaType === 'pptx';
            // Workbooks are wider than this popover. A fixed min-width box with
            // the viewer sized to the window clips the pinned horizontal
            // scrollbar, so the columns past the fold cannot be reached.
            const isSpreadsheetPreview = isSpreadsheetMediaItem({
              mediaType: previewMediaType,
              mediaUrl: typeof previewMediaUrl === 'string' ? previewMediaUrl : '',
            });
            return (
            <li key={index} className='box-border flex flex-row justify-left items-center gap-1 w-full p-0 m-0 rounded-sm border-1 border-white/5 border-solid'>
              <div className='flex flex-row justify-left items-center gap-1 w-full p-0 m-0'>
                <div className='box-border flex items-center w-full'>
                  <div
                    aria-label={t('sources.sourceNumber', { index: index + 1 })}
                    className={`cursor-default box-border flex items-center max-h-[30px] bg-transparent p-2 resize-none text-white/65 outline-none ${PATH_SEGMENT_TRUNCATE_CLASSNAME}`}
                  >
                    {sourceObject.truncatedFilePath ? sourceObject.truncatedFilePath.split('/').pop() : sourceObject.sourceUrl ? sourceObject.sourceUrl.split('/').pop() : t('sources.unknown')}
                    {formatSimplifiedSourceRowSuffix(sourceObject, t)}
                  </div>
                </div>
                <div className='flex flex-row justify-end items-center w-full p-0 m-0 gap-2'>
                  <Popover
                    placement='left-end'
                    showArrow={false}
                    shouldFlip={true}
                    offset={10}
                    crossOffset={150}
                    autoFocus={true}
                    className="outline-none"
                    classNames={{
                      base: `outline-none before:bg-gray-600 before:rounded-[2px] before:shadow-none before:drop-shadow-none
                      border-0 before:w-[25px] before:h-[25px] before:mt-[23px] before:mb-[-23px] mr-[-88px] sm:mr-0 h-screen sm:h-auto w-screen sm:w-auto`,
                    }}
                    isOpen={previewPopoverOpen === index}
                    // HeroUI popovers close on blur. The Capture Region dialog
                    // portals to document.body and takes focus, which would
                    // unmount this preview (and the dialog) before Save is reachable.
                    shouldCloseOnBlur={false}
                    shouldCloseOnScroll={false}
                    shouldCloseOnInteractOutside={(element) => {
                      // Player menus and the capture dialog also portal outside
                      // this preview. A click on Save must not dismiss it.
                      if (element.closest('.tp-popover')) return false;
                      if (element.closest('[data-source-preview-trigger]')) return false;
                      if (element.closest('.captureModalWrapper, .captureModalBase, .captureModalBackdrop')) {
                        return false;
                      }
                      return true;
                    }}
                    onOpenChange={(open) => {
                      if (!open) {
                        setPreviewPopoverOpen(-1);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        e.stopPropagation();
                        setPreviewPopoverOpen(-1);
                      }
                    }}
                  >
                    <PopoverTrigger>
                      <Button
                        type="button"
                        iconOnly
                        size="sm"
                        style="text"
                        color="light"
                        background="inverse"
                        aria-label={t('sources.previewNumber', { index: index + 1 })}
                        aria-haspopup="dialog"
                        data-source-preview-trigger=""
                        disabled={!isPreviewable}
                        className="box-border m-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isPreviewable) {
                            setPreviewPopoverOpen(index);
                          }
                        }}
                      >
                        {previewPopoverOpen === -1 ? (
                          <TpTooltip content={t('sources.previewShort')} placement="left" delayDuration={500}>
                            <MagnifyingGlassPlusIcon className='outline-none' />
                          </TpTooltip>
                        ) : (
                          <MagnifyingGlassPlusIcon className='outline-none' />
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className={`${isTranscriptCapablePreview ? 'w-[790px] max-w-[790px]' : 'w-[450px] max-w-[450px]'} ${MEDIA_PREVIEW_OVERLAY_SURFACE_CLASS} box-border rounded-md p-0 ${
                      isSpreadsheetPreview
                        ? 'flex h-[min(90vh,720px)] max-h-[min(90vh,720px)] min-h-[320px] flex-col overflow-hidden'
                        : 'max-h-[min(90vh,720px)] overflow-y-auto'
                    }`}>
                      <div
                        tabIndex={0}
                        className={`relative flex w-full min-w-0 flex-col items-stretch outline-none ${isSpreadsheetPreview ? 'h-full min-h-0' : 'justify-start'}`}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            e.stopPropagation();
                            setPreviewPopoverOpen(-1);
                          }
                        }}
                      >
                        <Button
                          type="button"
                          iconOnly
                          size="sm"
                          style="text"
                          color="light"
                          background="inverse"
                          className="absolute top-1 right-1 z-10"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewPopoverOpen(-1);
                          }}
                          aria-label={t('sources.closePreview')}
                        >
                          <XIcon />
                        </Button>
                        <div className="flex shrink-0 flex-row items-center justify-center px-10 w-full min-w-0 max-w-full">
                          <div className={`text-white/100 text-sm break-all bg-gray-900 rounded-sm my-1 p-1 text-center ${PATH_DISPLAY_WHITESPACE_CLASSNAME}`}>
                            {extractPathAfterBulk(sourceObject.truncatedFilePath || sourceObject.sourceUrl || 'Unknown', true).truncatedFilePath}
                          </div>
                        </div>
                        <div className={`previewContainer flex w-full min-w-0 max-w-full items-stretch ${
                          isSpreadsheetPreview
                            ? 'min-h-0 flex-1 flex-col overflow-hidden'
                            : isTranscriptCapablePreview
                              ? 'min-w-[760px] justify-center'
                              : 'h-[520px] min-h-[320px] min-w-[420px] justify-center overflow-hidden'
                        }`}>
                          <MediaDisplay
                            mediaItem={{
                              fileInfo: sourceObject.fileInfo,
                              mediaType: previewMediaType,
                              ...(hasDisplayableUrl
                                ? {
                                    mediaUrl: previewMediaUrl,
                                    mediaSrc: previewMediaUrl,
                                  }
                                : {}),
                              content: sourceObject.content,
                              mediaContext: sourceObject.sourceContext,
                              ...(preferredDisplayable &&
                              (preferred.selectedBy === 'previewFilePath' ||
                                preferred.selectedBy === 'dispatchedFilePath')
                                ? { previewFilePath: preferred.mediaUrl }
                                : {}),
                            } as MediaSource}
                            containerHeight={isTranscriptCapablePreview || isSpreadsheetPreview ? undefined : 520}
                            containerWidth={isTranscriptCapablePreview ? 760 : 420}
                            fillContainer={isSpreadsheetPreview}
                            isSourceCard={isPagedDocumentPreview}
                            isPreview={false}
                          />
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                  <TpTooltip content={t('sources.downloadShort')} placement="right" delayDuration={500}>
                    <Button
                      iconOnly
                      size="sm"
                      style="text"
                      color="light"
                      background="inverse"
                      className={`box-border p-0 sm:p-1 m-0 ${isRestrictedFileType(sourceObject) ? 'text-white/20' : ''}`}
                      aria-label={t('sources.download')}
                      onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleDownload(sourceObject) }}
                      disabled={isRestrictedFileType(sourceObject)}
                      >
                      <DownloadSimpleIcon className='outline-none' />
                    </Button>
                  </TpTooltip>
                </div>
              </div>
            </li>
            );
          })}
        </ol>
      </div>
    );
  }

  return (
    <div className='flex line-clamp-1 max-h-[30px]'>
      {newSources?.length > 0 ? (
        <span className='flex justify-left p-0 m-0'>
          {t(newSources.length === 1 ? 'sources.countOne' : 'sources.countMany', { count: newSources.length })}
        </span>
      ) : (
        <span className='flex justify-left p-0 m-0 text-white/65' data-testid="table-sources-add-media">
          {t('sources.addMedia')}
        </span>
      )}
    </div>
  );
};

SimplifiedSources.displayName = 'Sources';

export default SimplifiedSources;
